"use client";

import { useEffect } from "react";
import { BrandingEntityType } from "database";
import { useBrandingRoute } from "@/hooks/use-branding-route";
import { useBrandingStore, DEFAULT_BRANDING } from "@/zustand/brandingStore";
import { resolveBrandingCacheKey } from "@/lib/branding/cache-key";
import type { BrandingCacheKey } from "@/lib/branding/types";

/**
 * Watches the current route and ensures branding is loaded into the store
 * when needed. Skips fetch when already cached or hydrated from RSC.
 *
 * Note: Domain layout hydrates as WHITE_LABEL:<uuid>, while this route hook
 * often only has the subdomain — so WL cache hits by uuid may miss here and
 * we re-fetch via /api/branding (resolves domain → id). That's intentional.
 */
export function BrandingInitializer() {
  const route = useBrandingRoute();
  const ensureBranding = useBrandingStore((s) => s.ensureBranding);
  const setCurrentKey = useBrandingStore((s) => s.setCurrentKey);
  const hydrate = useBrandingStore((s) => s.hydrate);

  useEffect(() => {
    if (route.level === "DEFAULT" || !route.entityId) {
      const state = useBrandingStore.getState();
      if (state.currentKey !== "DEFAULT:default" || state.status !== "ready") {
        hydrate("DEFAULT", "default", DEFAULT_BRANDING, "DEFAULT");
      }
      return;
    }

    if (route.entityType === "DEFAULT") return;

    const key = resolveBrandingCacheKey(route.entityType, route.entityId);
    const state = useBrandingStore.getState();
    if (state.cache[key]) {
      setCurrentKey(key, route.level);
      return;
    }

    // Leaving /event/*: promote any cached WHITE_LABEL entry so we don't stay
    // stuck on EVENT branding until a network fetch completes.
    if (route.entityType === BrandingEntityType.WHITE_LABEL) {
      const wlKey = Object.keys(state.cache).find((k) =>
        k.startsWith(`${BrandingEntityType.WHITE_LABEL}:`)
      ) as BrandingCacheKey | undefined;
      if (wlKey) {
        setCurrentKey(wlKey, "WHITE_LABEL");
        return;
      }
    }

    if (!route.shouldFetch) return;

    void ensureBranding({
      entityId: route.entityId,
      entityType: route.entityType as BrandingEntityType,
      domain: route.domain ?? undefined,
    });
  }, [
    route.level,
    route.entityId,
    route.entityType,
    route.domain,
    route.shouldFetch,
    ensureBranding,
    setCurrentKey,
    hydrate,
  ]);

  return null;
}
